import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Popper,
    Select,
    Stack,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import DoneIcon from '@mui/icons-material/Done';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import {
    acceptMsiReconciliationSuccessor,
    acknowledgeMsiReconciliationRemovedRow,
    applyWatchlistRevision,
    deleteSecurityResolutionGroups,
    saveMsiSelectionsBatch,
} from '../utils/ApiCall';
import { parseCloudRunError } from './msiDriftReviewHelpers';
import ProviderIdentityReviewSection from './ProviderIdentityReviewSection';
import { parseWatchlistPublicationReceipt } from '../utils/watchlistPublication';
import { normalizeWatchlistLifecycle } from '../utils/watchlistLifecycle';
import {
    dispatchMsiSecurityResolutionAppliedEvent,
    WATCHLIST_CLIENT_PUBLICATION_REQUIRED_FIELD,
    WATCHLIST_DELTA_CLASSIFICATIONS,
    WATCHLIST_REVISION_PROGRESS_EVENT,
    parseWatchlistDeltaClassification,
    scopeSecurityOperationEventDetail,
} from '../utils/securityResolutionEvents';
import { useSecurityOperationContext } from '../context/SecurityOperationContext';
import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    APP_STYLE_PALETTE as MSI_PALETTE,
    usesAppStyleGrammar,
} from '../styles/msiStyleTokens';
import {
    getWatchlistMsiTone,
    watchlistTableFrameSx,
} from '../styles/watchlistMsiStyles';
import {
    getCountryChipDisplay,
    getSecurityStatusPresentation,
    renderCountryChipLabel,
} from './shared/securityDisplayPrimitives';

// ---------------------------------------------------------------------------
// Phase 4.5 status vocabulary (server-owned). The frontend never reimplements
// source-reason precedence; it renders exactly what the backend returns.
// ---------------------------------------------------------------------------
const STATE_READY = 'ready';
const STATE_PENDING = 'pending';
const STATE_NEEDS_ATTENTION = 'needs_attention';
const STATE_NOT_EVALUATED = 'not_evaluated';

const APPLY_RESULT_VISIBLE_MS = 6000;

function requireWatchlistLifecycle(payload, actionLabel) {
    try {
        return normalizeWatchlistLifecycle(payload);
    } catch (error) {
        throw new Error(
            `${actionLabel} returned an invalid Watchlist lifecycle: ${error.message}`,
        );
    }
}

function dispatchWatchlistRevisionProgressEvent(detail = {}, sessionScopeToken) {
    if (typeof window === 'undefined') {
        return;
    }
    const scopedDetail = scopeSecurityOperationEventDetail(detail, sessionScopeToken);
    if (!scopedDetail) return;
    window.dispatchEvent(new CustomEvent(WATCHLIST_REVISION_PROGRESS_EVENT, {
        detail: scopedDetail,
    }));
}

const nonNegativeCount = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
};

const compareText = (left, right) => String(left || '').localeCompare(
    String(right || ''),
    undefined,
    { numeric: true, sensitivity: 'base' },
);

const identityTypeText = (row) => String(
    row?.identifier_type || row?.inbound_id_type || '',
).trim().toUpperCase() || '—';

const identityValueText = (row) => String(
    row?.identifier
    || row?.inbound_id_value
    || row?.ticker
    || row?.display_name
    || '',
).trim() || '—';

const formatDateOnly = (value) => {
    const text = String(value || '').trim();
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
        return '';
    }
    return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
};

const identifierGroupBandSx = (shaded) => (
    shaded
        ? {
            backgroundColor: (theme) => (
                usesAppStyleGrammar(theme)
                    ? MSI_PALETTE.surfaceSubtle
                    : alpha(
                        theme.palette.text.primary,
                        theme.palette.mode === 'dark' ? 0.055 : 0.022,
                    )
            ),
        }
        : { backgroundColor: 'background.paper' }
);

// Worst-first ordering for a single source/derived state.
const STATE_SEVERITY = {
    [STATE_NEEDS_ATTENTION]: 0,
    [STATE_PENDING]: 1,
    [STATE_NOT_EVALUATED]: 2,
    [STATE_READY]: 3,
};

const stateOf = (status) => String(status?.state || STATE_NOT_EVALUATED);

// A selected row is an "issue" when any source or derived outcome is pending or
// needs attention. Input-issue rows are always issues.
const isSelectedIssueRow = (row) => {
    if (row?.row_kind === 'input_issue') {
        return true;
    }
    if (!row?.is_selected) {
        return false;
    }
    return STATUS_CHILDREN.some((child) => {
        const status = displayStatusForChild(row, child);
        const state = stateOf(status);
        return state === STATE_NEEDS_ATTENTION || state === STATE_PENDING;
    });
};

// Row priority (lower = higher): selected needs-attention, input issues,
// selected pending, selected ready, then everything else.
const rowPriority = (row) => {
    if (row?.row_kind === 'input_issue') {
        return 1;
    }
    if (!row?.is_selected) {
        return 5;
    }
    const worst = Math.min(
        STATE_SEVERITY[stateOf(row.identity)] ?? 3,
        STATE_SEVERITY[stateOf(row.market_data)] ?? 3,
        STATE_SEVERITY[stateOf(row.factor_data)] ?? 3,
    );
    if (worst === STATE_SEVERITY[STATE_NEEDS_ATTENTION]) {
        return 0;
    }
    if (worst === STATE_SEVERITY[STATE_PENDING]) {
        return 2;
    }
    return 3;
};

const STATUS_CHILDREN = [
    { key: 'identity', label: 'Identity', field: 'identity' },
    { key: 'market', label: 'Market Data', field: 'market_data' },
    { key: 'factor', label: 'Factor Data', field: 'factor_data' },
    { key: 'watchlist', label: 'Watchlist', field: 'watchlist_ready' },
    { key: 'portfolio', label: 'Portfolio Analysis', field: 'portfolio_analysis_ready' },
];

const displayStatusForChild = (row, child) => {
    const source = row?.[child.field] || {};
    if (child.key === 'portfolio' && stateOf(source) === STATE_PENDING) {
        return {
            state: STATE_NOT_EVALUATED,
            label: 'Not evaluated',
            detail: 'Portfolio analysis is evaluated after this security is included in a portfolio.',
        };
    }
    return source;
};

export const aggregateSecurityResolutionStatus = (row) => {
    const children = STATUS_CHILDREN.map((child) => ({
        ...child,
        status: displayStatusForChild(row, child),
    }));
    const worst = children.reduce((current, child) => {
        const severity = STATE_SEVERITY[stateOf(child.status)] ?? STATE_SEVERITY[STATE_NOT_EVALUATED];
        return severity < current.severity ? { child, severity } : current;
    }, { child: null, severity: Number.POSITIVE_INFINITY });
    const status = worst.child?.status || {
        state: STATE_NOT_EVALUATED,
        label: 'Not evaluated',
        detail: 'No status is available.',
    };
    // An otherwise ready row on imputed factor data stays flagged.
    const imputedFactor = children.find((child) => (
        child.key === 'factor' && child.status?.presentation === 'imputed'
    ));
    if (imputedFactor && stateOf(status) === STATE_READY) {
        return { status: imputedFactor.status, children };
    }
    return { status, children };
};

// Same transparent yellow as imputed Watchlist rows, kept opaque so sticky
// identity cells still cover the columns scrolling beneath them.
const imputedRowBackground = (theme) => {
    if (usesAppStyleGrammar(theme)) return MSI_PALETTE.warningBg;
    const tint = alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.14 : 0.08);
    return `linear-gradient(${tint}, ${tint}), ${theme.palette.background.paper}`;
};

const listingLabelForRow = (row) => row?.msi_listing_label || {};
const sortAccessor = (column, row) => {
    const label = listingLabelForRow(row);
    if (column === 'identity:type') return identityTypeText(row);
    if (column === 'identity:value') return identityValueText(row);
    if (column === 'country') return row.country || '';
    if (column === 'status') {
        return STATE_SEVERITY[stateOf(aggregateSecurityResolutionStatus(row).status)] ?? 9;
    }
    if (column === 'listing:ticker') return label.ticker || '';
    if (column === 'listing:security-name') return label.security_name || '';
    if (column === 'listing:market') return listingMarketText(label);
    if (column === 'listing:instrument') return label.normalized_instrument_type || '';
    if (column === 'listing:selection') return label.listing_selection_status || '';
    if (column === 'details') return row.status_detail || '';
    return '';
};

const DELETE_COLUMN_WIDTH = 76;
const IDENTITY_TYPE_COLUMN_WIDTH = 134;
const IDENTITY_VALUE_COLUMN_WIDTH = 142;
const COUNTRY_COLUMN_WIDTH = 102;
const SECURITY_GRID_WIDTHS_STORAGE_KEY = 'alphamizer.securityResolution.columnWidths.v3';
const SECURITY_GRID_MIN_COLUMN_WIDTH = 36;
const SECURITY_GRID_MAX_COLUMN_WIDTH = 420;
const SECURITY_GRID_ROW_HEIGHT = 38;
const SECURITY_GRID_HEADER_HEIGHT = 40;
const SECURITY_GRID_DEFAULT_VIEWPORT_HEIGHT = 660;
const SECURITY_GRID_OVERSCAN_ROWS = 8;
const SECURITY_GRID_MIN_WIDTHS = {
    'identity:type': 72,
    'identity:value': 88,
    country: 64,
    actions: 40,
};
// Each default fits its uppercase header label plus the sort arrow, so no
// header is clipped until the user narrows a column. A container narrower
// than the total scrolls horizontally instead.
const SECURITY_GRID_DEFAULT_WIDTHS = {
    'identity:type': IDENTITY_TYPE_COLUMN_WIDTH,
    'identity:value': IDENTITY_VALUE_COLUMN_WIDTH,
    country: COUNTRY_COLUMN_WIDTH,
    'listing:ticker': 92,
    'listing:security-name': 176,
    'listing:market': 152,
    'listing:instrument': 156,
    'listing:selection': 162,
    status: 90,
    details: 200,
    actions: DELETE_COLUMN_WIDTH,
};

const clampSecurityGridWidth = (columnKey, value) => Math.min(
    SECURITY_GRID_MAX_COLUMN_WIDTH,
    Math.max(
        SECURITY_GRID_MIN_WIDTHS[columnKey] || SECURITY_GRID_MIN_COLUMN_WIDTH,
        Math.round(Number(value) || 0),
    ),
);

const readStoredSecurityGridWidths = () => {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    try {
        const parsed = JSON.parse(
            window.localStorage.getItem(SECURITY_GRID_WIDTHS_STORAGE_KEY) || '{}',
        );
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        return Object.fromEntries(
            Object.entries(parsed)
                .filter(([, value]) => Number.isFinite(Number(value)))
                .map(([key, value]) => [key, clampSecurityGridWidth(key, value)]),
        );
    } catch (_error) {
        return {};
    }
};

const stickyIdentityBackground = (theme, { shaded = false, hover = false } = {}) => {
    if (usesAppStyleGrammar(theme)) {
        if (hover) return MSI_COMPONENT_COLORS.rowHover;
        return shaded ? MSI_PALETTE.surfaceSubtle : MSI_PALETTE.surface;
    }
    const overlays = [];
    if (hover) {
        overlays.push(alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.16 : 0.08));
    }
    if (shaded) {
        overlays.push(alpha(
            theme.palette.text.primary,
            theme.palette.mode === 'dark' ? 0.085 : 0.055,
        ));
    }
    const layers = overlays.map((color) => `linear-gradient(${color}, ${color})`);
    return [...layers, theme.palette.background.paper].join(', ');
};

const stickyIdentityCellSx = ({
    left,
    width,
    shaded = false,
    header = false,
    edge = false,
}) => ({
    position: 'sticky',
    left,
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
    zIndex: header ? 40 : 8,
    background: (theme) => stickyIdentityBackground(theme, { shaded }),
    backgroundClip: 'border-box',
    ...(edge ? {
        boxShadow: (theme) => `2px 0 0 ${alpha(theme.palette.divider, 0.55)}`,
    } : {}),
});

const STATISTIC_CHIP_TONE_TO_COLOR = {
    neutral: 'default',
    success: 'success',
    pending: 'warning',
    attention: 'error',
    primary: 'primary',
};

const statisticChipSx = (tone = 'neutral') => ({
    height: 26,
    maxWidth: '100%',
    borderRadius: 999,
    border: (theme) => {
        const msiTone = getWatchlistMsiTone(
            theme,
            tone === 'attention' ? 'danger' : tone === 'pending' ? 'warning' : tone === 'primary' ? 'info' : tone,
        );
        return msiTone ? `1px solid ${msiTone.border}` : 'none';
    },
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: 0,
    backgroundColor: (theme) => {
        const msiTone = getWatchlistMsiTone(
            theme,
            tone === 'attention' ? 'danger' : tone === 'pending' ? 'warning' : tone === 'primary' ? 'info' : tone,
        );
        if (msiTone) return msiTone.background;
        if (tone === 'neutral') {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.16)
                : alpha(theme.palette.text.primary, 0.1);
        }
        const paletteKey = STATISTIC_CHIP_TONE_TO_COLOR[tone] || 'primary';
        const main = theme.palette[paletteKey]?.main || theme.palette.primary.main;
        const opacity = theme.palette.mode === 'dark' ? 0.3 : 0.18;
        return alpha(main, opacity);
    },
    color: (theme) => {
        const msiTone = getWatchlistMsiTone(
            theme,
            tone === 'attention' ? 'danger' : tone === 'pending' ? 'warning' : tone === 'primary' ? 'info' : tone,
        );
        if (msiTone) return msiTone.foreground;
        if (tone === 'neutral') {
            return theme.palette.text.primary;
        }
        const paletteKey = STATISTIC_CHIP_TONE_TO_COLOR[tone] || 'primary';
        return theme.palette.mode === 'dark'
            ? (theme.palette[paletteKey]?.light || theme.palette.primary.light)
            : (theme.palette[paletteKey]?.dark || theme.palette.primary.dark);
    },
    '& .MuiChip-label': {
        px: 1.05,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    '& .MuiChip-icon': {
        color: 'inherit',
    },
});

const statisticChipTestId = (label) => `security-resolution-stat-${String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

function StatusDetailGlyph({ status }) {
    const state = stateOf(status);
    const presentation = getSecurityStatusPresentation(
        { code: status?.presentation || state, label: status?.label, tooltip: status?.detail },
    );
    const Glyph = presentation.icon;
    return (
        <Box
            component="span"
            aria-hidden="true"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                flex: '0 0 18px',
                color: (theme) => (
                    presentation.color === 'text'
                        ? theme.palette.text.secondary
                        : theme.palette[presentation.color]?.main || theme.palette.text.secondary
                ),
            }}
        >
            <Glyph sx={{ fontSize: 14 }} />
        </Box>
    );
}

StatusDetailGlyph.propTypes = {
    status: PropTypes.shape({
        state: PropTypes.string,
        label: PropTypes.string,
        detail: PropTypes.string,
        presentation: PropTypes.string,
    }),
};

function CombinedStatusCell({ row }) {
    const aggregate = aggregateSecurityResolutionStatus(row);
    const [detailsOpen, setDetailsOpen] = useState(false);
    return (
        <Tooltip
            arrow
            placement="right"
            open={detailsOpen}
            disableFocusListener
            disableHoverListener
            onClose={() => setDetailsOpen(false)}
            title={(
                <Stack spacing={0.55} sx={{ minWidth: 225, py: 0.35 }}>
                    {aggregate.children.map((child) => (
                        <Stack
                            key={child.key}
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                        >
                            <StatusDetailGlyph status={child.status} />
                            <Typography component="div" variant="caption" sx={{ fontWeight: 800 }}>
                                {child.label}: {child.status?.label || 'Not evaluated'}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            )}
        >
            <Box
                data-testid="security-resolution-combined-status"
                role="img"
                aria-label={aggregate.status?.label || 'Not evaluated'}
                tabIndex={0}
                onFocus={() => setDetailsOpen(true)}
                onBlur={() => setDetailsOpen(false)}
                onMouseEnter={() => setDetailsOpen(true)}
                onMouseLeave={() => setDetailsOpen(false)}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 22,
                    height: 22,
                    mx: 'auto',
                }}
            >
                <StatusDetailGlyph status={aggregate.status} />
            </Box>
        </Tooltip>
    );
}

CombinedStatusCell.propTypes = {
    row: PropTypes.object.isRequired,
};

function CountryCell({ country }) {
    if (!country) {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    const display = getCountryChipDisplay(country);
    return (
        <Tooltip title={display.tooltip} arrow describeChild>
            <Typography
                component="span"
                aria-label={display.tooltip}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                    lineHeight: 1.35,
                }}
            >
                {renderCountryChipLabel(display)}
            </Typography>
        </Tooltip>
    );
}

CountryCell.propTypes = {
    country: PropTypes.string,
};

const countrySelectionSecondaryText = (row) => {
    const label = row?.msi_listing_label || {};
    return [
        fieldText(label.security_name),
        fieldText(label.exchange_display_name),
        fieldText(label.segment_mic) || fieldText(label.operating_mic),
        // Feeds of one listing share its ticker (TKA.DE vs TKA.F); name the feed.
        fieldText(label.price_feed) || fieldText(label.ticker) || fieldText(row?.ticker),
        humanizeMsiLabelValue(label.normalized_instrument_type),
    ].filter((value) => value && value !== '—').join(' · ');
};

function CountrySelectionCell({
    country,
    currentCountry,
    options,
    blockedOptions,
    selectedRowKey,
    disabled,
    status,
    reason,
    onSelect,
}) {
    const selectableOptions = Array.isArray(options) ? options : [];
    const unavailableOptions = Array.isArray(blockedOptions) ? blockedOptions : [];
    const selected = selectableOptions.find((row) => row.row_key === selectedRowKey) || null;
    const draftChanged = Boolean(
        selected
        && String(selected.country || '').toUpperCase()
            !== String(currentCountry || '').toUpperCase(),
    );
    const selectedCountry = (
        draftChanged ? selected?.country : country || selected?.country
    ) || '';
    const selectedDisplay = getCountryChipDisplay(selectedCountry);
    const accessibleState = [
        selectedCountry ? `Country ${selectedCountry}` : 'Country unavailable',
        currentCountry && currentCountry !== selectedCountry
            ? `current Watchlist Country ${currentCountry}`
            : '',
        status ? `status ${status}` : '',
        reason || '',
    ].filter(Boolean).join('; ');
    return (
        <FormControl
            variant="standard"
            size="small"
            fullWidth
            disabled={disabled || selectableOptions.length === 0}
            sx={{ minWidth: 0 }}
        >
            <Select
                value={selected?.row_key || ''}
                displayEmpty
                disableUnderline
                onChange={(event) => onSelect?.(event.target.value)}
                inputProps={{
                    'aria-label': `Choose country and listing; ${accessibleState}`,
                }}
                renderValue={() => (
                    selectedCountry ? (
                        <Box
                            component="span"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}
                        >
                            {renderCountryChipLabel(selectedDisplay)}
                            <KeyboardArrowDownIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </Box>
                    ) : (
                        <Typography
                            component="span"
                            sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'warning.main' }}
                        >
                            Unavailable
                        </Typography>
                    )
                )}
                MenuProps={{
                    PaperProps: { sx: { maxHeight: 320, minWidth: 250 } },
                }}
                sx={{
                    minHeight: 28,
                    fontSize: '0.75rem',
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '0 !important',
                        py: 0.25,
                        pr: '18px !important',
                    },
                    '& .MuiSelect-icon': { display: 'none' },
                }}
            >
                {selectableOptions.length === 0 && unavailableOptions.length === 0 && (
                    <MenuItem value="" disabled>
                        No bounded Country alternatives
                    </MenuItem>
                )}
                {[...selectableOptions, ...unavailableOptions].map((row) => {
                    const display = getCountryChipDisplay(row.country);
                    const secondary = countrySelectionSecondaryText(row);
                    const unavailable = !row.selection_token && !row.is_selected;
                    return (
                        <MenuItem
                            key={row.row_key}
                            value={row.row_key}
                            disabled={unavailable}
                            dense
                        >
                            <Stack spacing={0} sx={{ minWidth: 0 }}>
                                <Typography component="span" sx={{ fontSize: '0.78rem' }}>
                                    {renderCountryChipLabel(display)}
                                </Typography>
                                {secondary && (
                                    <Typography
                                        component="span"
                                        color="text.secondary"
                                        sx={{ fontSize: '0.7rem' }}
                                    >
                                        {secondary}
                                    </Typography>
                                )}
                                {unavailable && row.selection_blocked_reason && (
                                    <Typography
                                        component="span"
                                        color="warning.main"
                                        sx={{ fontSize: '0.7rem' }}
                                    >
                                        {row.selection_blocked_reason}
                                    </Typography>
                                )}
                            </Stack>
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
}

CountrySelectionCell.propTypes = {
    country: PropTypes.string,
    currentCountry: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    blockedOptions: PropTypes.arrayOf(PropTypes.object),
    selectedRowKey: PropTypes.string,
    disabled: PropTypes.bool,
    status: PropTypes.string,
    reason: PropTypes.string,
    onSelect: PropTypes.func,
};

// ---------------------------------------------------------------------------
// Selection helpers (opaque tokens only — never a canonical id).
// ---------------------------------------------------------------------------
const serverSelectedTokenRowKeys = (rows, groupKey) => new Set(
    rows
        .filter((row) => row.group_key === groupKey
            && row.is_selected
            && row.selection_token)
        .map((row) => row.row_key),
);

// One security can trade on several exchanges (Xetra and Frankfurt both
// trade ThyssenKrupp). Each security uses one price feed: its primary venue.
const PRIMARY_VENUE_NOTE = (
    'Prices come from each security\u2019s primary exchange, for example Xetra '
    + 'for German stocks. Other exchanges trade the same shares but report only '
    + 'their own, much smaller volume, so the primary exchange keeps ADTV accurate.'
);

const fieldText = (value) => {
    const text = String(value ?? '').trim();
    return text || '';
};

const humanizeMsiLabelValue = (value) => {
    const text = fieldText(value);
    if (!text) return '—';
    return text
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());
};

const listingMarketText = (label) => {
    if (!label || typeof label !== 'object') return '—';
    if (fieldText(label.listing_selection_status).toLowerCase() === 'ambiguous') {
        return 'Venue unresolved';
    }
    const preferredMic = fieldText(label.segment_mic) || fieldText(label.operating_mic);
    const parts = [
        fieldText(label.exchange_display_name),
        preferredMic,
        fieldText(label.country_code),
        fieldText(label.currency_code),
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : '—';
};

const buildAttemptId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `security-resolution-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export default function WatchlistSecurityStatusTable({
    resolutionCenter = null,
    selectedFactorVendor = null,
    onRefresh = null,
    focusTarget = null,
}) {
    const { sessionScopeToken } = useSecurityOperationContext();
    const [sort, setSort] = useState({ column: 'identity:value', direction: 'asc' });
    const [columnWidths, setColumnWidths] = useState(readStoredSecurityGridWidths);
    const resizeCleanupRef = useRef(null);
    const [revealedGridValue, setRevealedGridValue] = useState(null);
    const [search, setSearch] = useState('');
    const [showOnlyIssues, setShowOnlyIssues] = useState(false);
    const securityGridScrollRef = useRef(null);
    const securityGridScrollTopRef = useRef(0);
    const securityGridScrollFrameRef = useRef(null);
    const securityGridPendingViewportRef = useRef(null);
    const [securityGridViewport, setSecurityGridViewport] = useState({
        scrollTop: 0,
        height: SECURITY_GRID_DEFAULT_VIEWPORT_HEIGHT,
    });
    // group_key -> Set(row_key) of the user's working selection (token rows only).
    const [draftSelections, setDraftSelections] = useState({});
    const [applying, setApplying] = useState(false);
    const applyingRef = useRef(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [applyResultMessage, setApplyResultMessage] = useState(null);
    const applyAttemptIdRef = useRef(null);
    const [deletionDraftByToken, setDeletionDraftByToken] = useState({});
    const deleteAttemptRef = useRef(null);

    const columnWidth = useCallback((columnKey) => (
        columnWidths[columnKey]
        || SECURITY_GRID_DEFAULT_WIDTHS[columnKey]
        || (columnKey.startsWith('field:') ? 104 : 96)
    ), [columnWidths]);

    const gridCellSx = useCallback((columnKey, extra = {}) => {
        const width = columnWidth(columnKey);
        return {
            width,
            minWidth: width,
            maxWidth: width,
            boxSizing: 'border-box',
            overflow: 'hidden',
            px: 1,
            py: 0.5,
            verticalAlign: 'middle',
            ...extra,
        };
    }, [columnWidth]);

    const stickyGridCellSx = useCallback((columnKey, options) => ({
        ...gridCellSx(columnKey),
        ...stickyIdentityCellSx({
            ...options,
            width: columnWidth(columnKey),
        }),
    }), [columnWidth, gridCellSx]);

    const handleColumnResizeStart = useCallback((event, columnKey) => {
        event.preventDefault();
        event.stopPropagation();
        resizeCleanupRef.current?.();
        const startX = Number(event.clientX || 0);
        const startWidth = columnWidth(columnKey);
        const scheduleFrame = typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : (callback) => window.setTimeout(callback, 16);
        const cancelFrame = typeof window.cancelAnimationFrame === 'function'
            ? window.cancelAnimationFrame.bind(window)
            : window.clearTimeout.bind(window);
        let pendingWidth = startWidth;
        let frameId = null;
        const commitWidth = () => {
            frameId = null;
            setColumnWidths((current) => (
                current[columnKey] === pendingWidth
                    ? current
                    : { ...current, [columnKey]: pendingWidth }
            ));
        };
        const move = (moveEvent) => {
            pendingWidth = clampSecurityGridWidth(
                columnKey,
                startWidth + Number(moveEvent.clientX || 0) - startX,
            );
            if (frameId == null) frameId = scheduleFrame(commitWidth);
        };
        const cleanup = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', cleanup);
            if (frameId != null) {
                cancelFrame(frameId);
                commitWidth();
            }
            resizeCleanupRef.current = null;
        };
        resizeCleanupRef.current = cleanup;
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', cleanup);
    }, [columnWidth]);

    const resizeColumnBy = useCallback((columnKey, delta) => {
        setColumnWidths((current) => {
            const currentWidth = current[columnKey]
                || SECURITY_GRID_DEFAULT_WIDTHS[columnKey]
                || (columnKey.startsWith('field:') ? 104 : 96);
            return {
                ...current,
                [columnKey]: clampSecurityGridWidth(columnKey, currentWidth + delta),
            };
        });
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            window.localStorage.setItem(
                SECURITY_GRID_WIDTHS_STORAGE_KEY,
                JSON.stringify(columnWidths),
            );
        } catch (_error) {
            // Private browsing or a full storage quota must not break the grid.
        }
    }, [columnWidths]);

    useEffect(() => () => resizeCleanupRef.current?.(), []);

    const [searchPanelOpen, setSearchPanelOpen] = useState(false);

    // Reconciliation review ("Removed by security-master updates") — MSI-owned.
    const [selectedSuccessorByRow, setSelectedSuccessorByRow] = useState({});
    const [reviewActionRowId, setReviewActionRowId] = useState(null);
    const reconciliationSectionRef = useRef(null);

    const handleToggleSearchPanel = useCallback(() => {
        if (searchPanelOpen) {
            setSearchPanelOpen(false);
            setSearch('');
            return;
        }
        setSearchPanelOpen(true);
    }, [searchPanelOpen]);

    const loading = Boolean(resolutionCenter?.loading);
    const fetchError = resolutionCenter?.error || '';
    const status = resolutionCenter?.status || 'ok';
    const summary = resolutionCenter?.summary || {};
    const rows = useMemo(
        () => (Array.isArray(resolutionCenter?.rows) ? resolutionCenter.rows : []),
        [resolutionCenter?.rows],
    );
    const listingLabelsEnabled = useMemo(
        () => rows.some((row) => row?.msi_listing_label != null),
        [rows],
    );
    const expectedWatchlistRevisionId = (
        resolutionCenter?.watchlist_field_contract?.expected_watchlist_revision_id
        || null
    );
    const tableColumnKeys = useMemo(() => [
        'identity:type',
        'identity:value',
        'country',
        ...(listingLabelsEnabled ? [
            'listing:ticker',
            'listing:security-name',
            'listing:market',
            'listing:instrument',
            'listing:selection',
        ] : []),
        'status',
        'details',
        'actions',
    ], [listingLabelsEnabled]);
    // Full-width rows (virtual spacers, the no-match message) must span exactly
    // the header's columns. In the fixed table layout an extra spanned column
    // has no width of its own, so it absorbs all spare width as a blank band.
    const tableColumnCount = tableColumnKeys.length;
    const tableMinWidth = useMemo(
        () => tableColumnKeys.reduce((total, columnKey) => total + columnWidth(columnKey), 0),
        [columnWidth, tableColumnKeys],
    );
    const groupsIndex = useMemo(
        () => (Array.isArray(resolutionCenter?.groups) ? resolutionCenter.groups : []),
        [resolutionCenter?.groups],
    );
    const groupTokenByKey = useMemo(() => {
        const map = {};
        groupsIndex.forEach((group) => {
            if (group?.group_key) {
                map[group.group_key] = group.selection_group_token || null;
            }
        });
        return map;
    }, [groupsIndex]);
    const selectionOptionsByGroup = useMemo(() => {
        const map = new Map();
        rows.forEach((row) => {
            if (
                !row?.group_key
                || !row?.row_key
                || (!row?.selection_token && !row?.is_selected)
            ) return;
            if (!map.has(row.group_key)) map.set(row.group_key, []);
            map.get(row.group_key).push(row);
        });
        return map;
    }, [rows]);
    // Options that failed a pre-selection check stay visible, greyed, with why.
    const blockedOptionsByGroup = useMemo(() => {
        const map = new Map();
        rows.forEach((row) => {
            if (
                !row?.group_key
                || !row?.row_key
                || !row?.resolution_candidate
                || row?.selection_token
                || !row?.selection_blocked_reason
            ) return;
            if (!map.has(row.group_key)) map.set(row.group_key, []);
            map.get(row.group_key).push(row);
        });
        return map;
    }, [rows]);
    const selectedOptionRowKeyByGroup = useMemo(() => {
        const map = new Map();
        selectionOptionsByGroup.forEach((options, groupKey) => {
            const draft = draftSelections[groupKey] instanceof Set
                ? draftSelections[groupKey]
                : serverSelectedTokenRowKeys(rows, groupKey);
            const selected = options.find((row) => draft.has(row.row_key));
            if (selected) map.set(groupKey, selected.row_key);
        });
        return map;
    }, [draftSelections, rows, selectionOptionsByGroup]);
    const reconciliationRows = useMemo(
        () => (
            Array.isArray(resolutionCenter?.reconciliation_review?.rows)
                ? resolutionCenter.reconciliation_review.rows
                : []
        ),
        [resolutionCenter?.reconciliation_review?.rows],
    );
    const reconciliationRowCount = reconciliationRows.length;
    // Reset the working selection whenever the server's selected set changes
    // (e.g. after an apply/refresh). A background poll that does not change the
    // selection preserves in-progress toggles.
    const serverSelectionSignature = useMemo(() => {
        const byGroup = {};
        rows.forEach((row) => {
            if (row.is_selected && row.selection_token) {
                (byGroup[row.group_key] = byGroup[row.group_key] || []).push(row.row_key);
            }
        });
        return JSON.stringify(
            Object.keys(byGroup)
                .sort()
                .map((key) => [key, byGroup[key].sort()]),
        );
    }, [rows]);
    const optionSurfaceSignature = useMemo(() => JSON.stringify({
        groups: Object.keys(groupTokenByKey)
            .sort()
            .map((groupKey) => [groupKey, groupTokenByKey[groupKey] || null]),
        rows: rows
            .filter((row) => row.selection_token)
            .map((row) => [row.group_key, row.row_key, row.selection_token])
            .sort((a, b) => `${a[0]}::${a[1]}`.localeCompare(`${b[0]}::${b[1]}`)),
    }), [groupTokenByKey, rows]);
    const previousServerSelectionSignatureRef = useRef(null);
    useEffect(() => {
        const serverSelectionChanged = previousServerSelectionSignatureRef.current !== serverSelectionSignature;
        previousServerSelectionSignatureRef.current = serverSelectionSignature;
        const validRowKeysByGroup = new Map();
        rows.forEach((row) => {
            if (!row?.group_key || !row?.row_key || !row?.selection_token) {
                return;
            }
            if (!validRowKeysByGroup.has(row.group_key)) {
                validRowKeysByGroup.set(row.group_key, new Set());
            }
            validRowKeysByGroup.get(row.group_key).add(row.row_key);
        });

        setDraftSelections((current) => {
            const next = {};
            const seen = new Set();
            rows.forEach((row) => {
                if (seen.has(row.group_key)) {
                    return;
                }
                seen.add(row.group_key);
                const serverSelection = serverSelectedTokenRowKeys(rows, row.group_key);
                const draft = current[row.group_key];
                if (serverSelectionChanged || !(draft instanceof Set)) {
                    next[row.group_key] = serverSelection;
                    return;
                }
                const validRowKeys = validRowKeysByGroup.get(row.group_key) || new Set();
                const hasMissingDraftRow = [...draft].some((rowKey) => !validRowKeys.has(rowKey));
                next[row.group_key] = hasMissingDraftRow
                    ? serverSelection
                    : new Set(draft);
            });
            return next;
        });
    }, [serverSelectionSignature, optionSurfaceSignature]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (focusTarget !== 'reconciliation' || !reconciliationSectionRef.current) {
            return;
        }
        reconciliationSectionRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, [focusTarget, reconciliationRowCount]);

    useEffect(() => {
        if (!applyResultMessage) {
            return undefined;
        }
        const timer = window.setTimeout(() => setApplyResultMessage(null), APPLY_RESULT_VISIBLE_MS);
        return () => window.clearTimeout(timer);
    }, [applyResultMessage]);

    const handleSelectGroupOption = useCallback((groupKey, rowKey) => {
        const option = (selectionOptionsByGroup.get(groupKey) || [])
            .find((row) => row.row_key === rowKey && row.selection_token);
        if (!option) return;
        setErrorMessage('');
        setApplyResultMessage(null);
        applyAttemptIdRef.current = null;
        setDraftSelections((current) => ({
            ...current,
            [groupKey]: new Set([rowKey]),
        }));
    }, [selectionOptionsByGroup]);

    // Groups whose working selection differs from the server's selection.
    const changedGroups = useMemo(() => {
        const changed = [];
        Object.keys(draftSelections).forEach((groupKey) => {
            const draft = draftSelections[groupKey] instanceof Set
                ? draftSelections[groupKey]
                : serverSelectedTokenRowKeys(rows, groupKey);
            if (!(draft instanceof Set)) {
                return;
            }
            const initial = serverSelectedTokenRowKeys(rows, groupKey);
            const sameSize = draft.size === initial.size;
            const sameMembers = sameSize && [...draft].every((key) => initial.has(key));
            if (!sameMembers) {
                changed.push(groupKey);
            }
        });
        return changed;
    }, [draftSelections, rows]);

    useEffect(() => {
        const currentDeleteTokens = new Set(
            rows
                .filter((row) => row?.delete_group_token)
                .map((row) => String(row.delete_group_token)),
        );
        setDeletionDraftByToken((current) => {
            if (Object.keys(current).length === 0) return current;
            const next = Object.fromEntries(
                Object.entries(current).filter(([token]) => currentDeleteTokens.has(token)),
            );
            if (Object.keys(current).length === Object.keys(next).length) return current;
            deleteAttemptRef.current = null;
            return next;
        });
    }, [rows]);

    const stagedDeletionGroupKeys = useMemo(
        () => new Set(
            Object.values(deletionDraftByToken).map((draft) => draft.groupKey),
        ),
        [deletionDraftByToken],
    );
    const pendingChangeCount = useMemo(
        () => new Set([
            ...changedGroups,
            ...stagedDeletionGroupKeys,
        ]).size,
        [changedGroups, stagedDeletionGroupKeys],
    );

    const toggleDeletionDraft = useCallback((row) => {
        const token = String(row?.delete_group_token || '');
        const groupKey = String(row?.group_key || '');
        if (!token || !groupKey || applyingRef.current) return;
        setErrorMessage('');
        setApplyResultMessage(null);
        setDeletionDraftByToken((current) => {
            const next = { ...current };
            if (next[token]) {
                delete next[token];
            } else {
                next[token] = {
                    deleteToken: token,
                    groupKey,
                    confirmedPortfolioRemoval: Boolean(
                        row?.delete_will_remove_portfolio,
                    ),
                };
            }
            return next;
        });
        deleteAttemptRef.current = null;
    }, []);

    const handleClearChanges = useCallback(() => {
        setErrorMessage('');
        setApplyResultMessage(null);
        applyAttemptIdRef.current = null;
        deleteAttemptRef.current = null;
        setDeletionDraftByToken({});
        const next = {};
        const seen = new Set();
        rows.forEach((row) => {
            if (seen.has(row.group_key)) {
                return;
            }
            seen.add(row.group_key);
            next[row.group_key] = serverSelectedTokenRowKeys(rows, row.group_key);
        });
        setDraftSelections(next);
    }, [rows]);

    // One Save batches dismissals and applies the remaining identity selections.
    const handleApply = useCallback(async () => {
        if (pendingChangeCount === 0 || applyingRef.current) {
            return;
        }
        const selectionGroups = changedGroups.filter(
            (groupKey) => !stagedDeletionGroupKeys.has(groupKey),
        );
        const tokenRowByKey = new Map();
        rows.forEach((row) => {
            if (row.selection_token) {
                tokenRowByKey.set(`${row.group_key}::${row.row_key}`, row.selection_token);
            }
        });
        const selections = [];
        let missingToken = false;
        selectionGroups.forEach((groupKey) => {
            const groupToken = groupTokenByKey[groupKey];
            const draft = draftSelections[groupKey];
            if (!groupToken || !(draft instanceof Set)) {
                missingToken = true;
                return;
            }
            const selectionTokens = [];
            draft.forEach((rowKey) => {
                const token = tokenRowByKey.get(`${groupKey}::${rowKey}`);
                if (!token) {
                    missingToken = true;
                    return;
                }
                selectionTokens.push(token);
            });
            selections.push({
                group_token: groupToken,
                selection_tokens: selectionTokens,
            });
        });
        if (missingToken || (selectionGroups.length > 0 && selections.length === 0)) {
            setErrorMessage(
                'These options are out of date. Refresh Security Resolution and try again.',
            );
            return;
        }

        applyingRef.current = true;
        setApplying(true);
        setErrorMessage('');
        let deletionsCommitted = false;
        let refreshedAfterDeletion = false;
        try {
            const deletions = Object.values(deletionDraftByToken);
            if (deletions.length > 0) {
                const requestKey = JSON.stringify(
                    deletions
                        .map((deletion) => deletion.deleteToken)
                        .sort(),
                );
                if (deleteAttemptRef.current?.requestKey !== requestKey) {
                    deleteAttemptRef.current = {
                        requestKey,
                        attemptId: buildAttemptId(),
                    };
                }
                const deleteResponse = await deleteSecurityResolutionGroups({
                    deletions,
                    attemptId: deleteAttemptRef.current.attemptId,
                });
                if (deleteResponse?.success !== true) {
                    const parsed = parseCloudRunError(
                        deleteResponse?.message || deleteResponse,
                    );
                    setErrorMessage(
                        parsed.message || 'Failed to dismiss submitted securities.',
                    );
                    return;
                }
                deletionsCommitted = true;
                setDraftSelections((current) => Object.fromEntries(
                    Object.entries(current).filter(
                        ([groupKey]) => !stagedDeletionGroupKeys.has(groupKey),
                    ),
                ));
                setDeletionDraftByToken({});
                deleteAttemptRef.current = null;
                if (deleteResponse?.watchlist_changed) {
                    setErrorMessage(
                        'Active Watchlist removals must be staged from the Watchlist table.',
                    );
                    return;
                }
            }
            if (selections.length === 0) {
                applyAttemptIdRef.current = null;
                setApplyResultMessage({
                    severity: 'success',
                    text: `${deletions.length} submitted securit${deletions.length === 1 ? 'y' : 'ies'} dismissed from Security Resolution.`,
                });
                await onRefresh?.({ force: true });
                refreshedAfterDeletion = true;
                return;
            }
            const selectionRequestKey = JSON.stringify(
                selections
                    .map((selection) => ({
                        group_token: selection.group_token,
                        selection_tokens: [...selection.selection_tokens].sort(),
                    }))
                    .sort((left, right) => (
                        left.group_token.localeCompare(right.group_token)
                    )),
            );
            if (applyAttemptIdRef.current?.requestKey !== selectionRequestKey) {
                applyAttemptIdRef.current = {
                    requestKey: selectionRequestKey,
                    attemptId: buildAttemptId(),
                };
            }
            const response = await saveMsiSelectionsBatch({
                selections,
                attemptId: applyAttemptIdRef.current.attemptId,
                expectedWatchlistRevisionId,
            });
            if (response?.success !== true) {
                const parsed = parseCloudRunError(response?.message || response);
                setErrorMessage(parsed.message || 'Failed to apply identity choices.');
                return;
            }
            const stagedLifecycle = requireWatchlistLifecycle(
                response,
                'Identity choices',
            );
            const approvalRequired = stagedLifecycle.status === 'pending_review';
            if (
                approvalRequired
                && (
                    typeof response?.watchlist_revision_id !== 'string'
                    || !response.watchlist_revision_id.trim()
                )
            ) {
                setErrorMessage(
                    'The identity-choice response is missing its Watchlist revision. Retry the same Save action.',
                );
                return;
            }
            let applyResponse = null;
            if (approvalRequired) {
                applyResponse = await applyWatchlistRevision({
                    revisionId: response.watchlist_revision_id,
                    attemptId: `${applyAttemptIdRef.current.attemptId}:apply`,
                    expectedWatchlistRevisionId:
                        response?.expected_watchlist_revision_id || null,
                });
                if (applyResponse?.success !== true) {
                    const parsed = parseCloudRunError(
                        applyResponse?.message || applyResponse,
                    );
                    setErrorMessage(
                        parsed.message
                        || 'The identity choices were staged, but processing could not start.',
                    );
                    return;
                }
            }
            const appliedRows = nonNegativeCount(response?.applied_watchlist_rows);
            const addedRows = nonNegativeCount(response?.added_watchlist_rows);
            const removedRows = nonNegativeCount(response?.removed_watchlist_rows);
            const fieldChangedRows = nonNegativeCount(response?.field_changed_rows);
            const categorizationChangedRows = nonNegativeCount(
                response?.categorization_changed_rows,
            );
            const customMetadataChangedRows = nonNegativeCount(
                response?.custom_metadata_changed_rows,
            );
            const lifecycle = requireWatchlistLifecycle(
                applyResponse ?? response,
                'Identity choices',
            );
            const processingStarted = ['queued', 'running'].includes(lifecycle.status);
            const responseSettled = ['succeeded', 'unchanged'].includes(lifecycle.status);
            const classificationResult = responseSettled
                ? parseWatchlistDeltaClassification({
                    ...response,
                    ...applyResponse,
                    lifecycle,
                })
                : { classification: null, error: null };
            const deltaClassification = classificationResult.classification;
            const publishesSettledDelta = [
                'membership_changed',
                'categorization_changed',
                'custom_metadata_changed',
                'no_change',
            ].includes(deltaClassification);
            if (
                responseSettled && classificationResult.error
            ) {
                setErrorMessage(
                    `${classificationResult.error} Retry the same Save action.`,
                );
                return;
            }
            if (
                responseSettled
                && deltaClassification === WATCHLIST_DELTA_CLASSIFICATIONS.NO_CHANGE
            ) {
                dispatchMsiSecurityResolutionAppliedEvent({
                    lifecycle,
                    status: lifecycle.status,
                    phase: lifecycle.phase,
                    delta_classification: deltaClassification,
                }, sessionScopeToken);
            }
            if (
                processingStarted
                || (
                    publishesSettledDelta
                    && deltaClassification !== WATCHLIST_DELTA_CLASSIFICATIONS.NO_CHANGE
                )
            ) {
                if (!processingStarted && !responseSettled) {
                    setErrorMessage(
                        'The identity choices returned an invalid publication status. Retry the same Save action.',
                    );
                    return;
                }
                const publicationReceipt = parseWatchlistPublicationReceipt(lifecycle);
                if (!publicationReceipt) {
                    setErrorMessage(
                        'The identity choices were saved without a valid Watchlist revision receipt. Retry the same Save action.',
                    );
                    return;
                }
                const parentOperationId = lifecycle.operation_id;
                const parentOperationType = applyResponse?.operation_type
                    ?? response?.operation_type
                    ?? null;
                const watchlistMutationDelta = {
                    ...(response?.watchlist_mutation_delta || {}),
                    delta_classification: deltaClassification,
                    applied_watchlist_rows: appliedRows,
                    added_watchlist_rows: addedRows,
                    removed_watchlist_rows: removedRows,
                    field_changed_rows: fieldChangedRows,
                    categorization_changed_rows: categorizationChangedRows,
                    custom_metadata_changed_rows: customMetadataChangedRows,
                };
                const publicationDetail = {
                    lifecycle,
                    status: lifecycle.status,
                    phase: lifecycle.phase,
                    operation_id: parentOperationId,
                    operation_type: parentOperationType,
                    upload_run_id: response?.upload_run_id ?? null,
                    attempt_id: response?.attempt_id ?? null,
                    watchlist_revision_id: publicationReceipt.revisionId,
                    revision_seq: publicationReceipt.revisionSeq,
                    applied_watchlist_rows: appliedRows,
                    added_watchlist_rows: addedRows,
                    removed_watchlist_rows: removedRows,
                    field_changed_rows: fieldChangedRows,
                    categorization_changed_rows: categorizationChangedRows,
                    custom_metadata_changed_rows: customMetadataChangedRows,
                    ...(processingStarted ? {} : {
                        watchlist_mutation_delta: watchlistMutationDelta,
                        delta_classification: deltaClassification,
                    }),
                };
                if (processingStarted) {
                    dispatchWatchlistRevisionProgressEvent(publicationDetail, sessionScopeToken);
                } else {
                    dispatchMsiSecurityResolutionAppliedEvent(
                        publicationDetail,
                        sessionScopeToken,
                    );
                }
            }
            applyAttemptIdRef.current = null;
            try {
                await onRefresh?.({ force: true });
                refreshedAfterDeletion = true;
            } catch (refreshError) {
                const parsed = parseCloudRunError(refreshError);
                setErrorMessage(
                    parsed.message
                    || 'Identity choices were applied, but the latest status could not be loaded.',
                );
            }
        } catch (error) {
            const parsed = parseCloudRunError(error);
            setErrorMessage(parsed.message || 'Failed to apply identity choices.');
        } finally {
            if (deletionsCommitted && !refreshedAfterDeletion) {
                try {
                    await onRefresh?.({ force: true });
                } catch (_refreshError) {
                    // The committed dismissal remains authoritative; the next
                    // scheduled or manual refresh will reconcile the table.
                }
            }
            applyingRef.current = false;
            setApplying(false);
        }
    }, [
        changedGroups,
        deletionDraftByToken,
        draftSelections,
        expectedWatchlistRevisionId,
        groupTokenByKey,
        onRefresh,
        pendingChangeCount,
        rows,
        stagedDeletionGroupKeys,
        sessionScopeToken,
    ]);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof onRefresh !== 'function') return undefined;
        const refresh = () => { void onRefresh({ force: true }); };
        window.addEventListener('portfolio-import-applied', refresh);
        window.addEventListener('imports-updated', refresh);
        return () => {
            window.removeEventListener('portfolio-import-applied', refresh);
            window.removeEventListener('imports-updated', refresh);
        };
    }, [onRefresh]);

    // ---- Reconciliation review handlers (MSI-owned mutations) -------------
    const handleSelectSuccessor = useCallback((rowId, candidateId) => {
        setSelectedSuccessorByRow((current) => ({ ...current, [rowId]: candidateId }));
    }, []);

    const handleAcknowledgeReconciliationRow = useCallback(async (row) => {
        if (!row?.row_id || reviewActionRowId) {
            return;
        }
        setReviewActionRowId(row.row_id);
        setErrorMessage('');
        try {
            const response = await acknowledgeMsiReconciliationRemovedRow({ rowId: row.row_id });
            if (response?.success === false) {
                const parsed = parseCloudRunError(response?.message || response);
                setErrorMessage(parsed.message || 'Failed to acknowledge removed security.');
                return;
            }
            await onRefresh?.({ force: true });
        } catch (error) {
            const parsed = parseCloudRunError(error);
            setErrorMessage(
                parsed.message || 'Failed to acknowledge removed security.',
            );
        } finally {
            setReviewActionRowId(null);
        }
    }, [onRefresh, reviewActionRowId]);

    const handleAcceptSuccessor = useCallback(async (row) => {
        if (!row?.row_id || reviewActionRowId) {
            return;
        }
        const candidates = Array.isArray(row?.candidates) ? row.candidates : [];
        const candidateId = selectedSuccessorByRow[row.row_id]
            || candidates[0]?.candidate_id
            || '';
        if (!candidateId) {
            setErrorMessage('Choose a successor before replacing this security.');
            return;
        }
        setReviewActionRowId(row.row_id);
        setErrorMessage('');
        try {
            const response = await acceptMsiReconciliationSuccessor({ rowId: row.row_id, candidateId });
            let stagedLifecycle = null;
            if (response?.success === true) {
                stagedLifecycle = requireWatchlistLifecycle(
                    response,
                    'The replacement',
                );
            }
            if (
                response?.success !== true
                || stagedLifecycle?.status !== 'pending_review'
                || typeof response?.watchlist_revision_id !== 'string'
                || !response.watchlist_revision_id.trim()
            ) {
                const parsed = parseCloudRunError(response?.message || response);
                setErrorMessage(
                    (response?.success === true
                        ? 'The replacement is missing its staged Watchlist revision. Retry the same replacement.'
                        : parsed.message)
                    || 'Failed to replace removed security.',
                );
                return;
            }
            const applyResponse = await applyWatchlistRevision({
                revisionId: response.watchlist_revision_id,
                attemptId: `${response.attempt_id}:apply`,
                expectedWatchlistRevisionId:
                    response?.expected_watchlist_revision_id || null,
            });
            if (applyResponse?.success !== true) {
                const parsed = parseCloudRunError(applyResponse?.message || applyResponse);
                setErrorMessage(
                    parsed.message
                    || 'The replacement was staged, but Watchlist processing could not start.',
                );
                return;
            }
            const lifecycle = requireWatchlistLifecycle(
                applyResponse,
                'The replacement',
            );
            if (!['queued', 'running', 'succeeded', 'unchanged'].includes(lifecycle.status)) {
                setErrorMessage(
                    'The replacement returned an invalid processing status. Retry the same replacement.',
                );
                return;
            }
            const publicationReceipt = parseWatchlistPublicationReceipt(lifecycle);
            if (!publicationReceipt) {
                setErrorMessage(
                    'The replacement is missing its Watchlist revision receipt. Retry the same replacement.',
                );
                return;
            }
            const processingStarted = ['queued', 'running'].includes(lifecycle.status);
            const classification = processingStarted
                ? { classification: null, error: null }
                : parseWatchlistDeltaClassification({ ...response, ...applyResponse, lifecycle });
            if (classification.error) {
                setErrorMessage(`${classification.error} Retry the same replacement.`);
                return;
            }
            const deltaClassification = classification.classification;
            const publicationDetail = {
                lifecycle,
                status: lifecycle.status,
                phase: lifecycle.phase,
                operation_id: lifecycle.operation_id,
                operation_type: applyResponse?.operation_type
                    ?? response?.operation_type
                    ?? null,
                watchlist_revision_id: publicationReceipt.revisionId,
                revision_seq: publicationReceipt.revisionSeq,
                ...(processingStarted ? {} : {
                    delta_classification: deltaClassification,
                    watchlist_mutation_delta: {
                        ...(response?.watchlist_mutation_delta || {}),
                        delta_classification: deltaClassification,
                        repaired_count: 1,
                    },
                }),
            };
            if (processingStarted) {
                dispatchWatchlistRevisionProgressEvent(
                    publicationDetail,
                    sessionScopeToken,
                );
            } else {
                dispatchMsiSecurityResolutionAppliedEvent(
                    publicationDetail,
                    sessionScopeToken,
                );
            }
            await onRefresh?.({ force: true });
        } catch (error) {
            const parsed = parseCloudRunError(error);
            setErrorMessage(
                parsed.message || 'Failed to replace removed security.',
            );
        } finally {
            setReviewActionRowId(null);
        }
    }, [onRefresh, reviewActionRowId, selectedSuccessorByRow, sessionScopeToken]);

    // ---- Display model: group rows, filter, sort --------------------------
    const searchMatches = useCallback((row) => {
        const needle = search.trim().toLowerCase();
        if (!needle) {
            return true;
        }
        return [row.ticker, row.display_name, row.identifier, row.country]
            .some((value) => String(value || '').toLowerCase().includes(needle));
    }, [search]);

    const groupsForDisplay = useMemo(() => {
        const orderByKey = new Map();
        groupsIndex.forEach((group, index) => orderByKey.set(group.group_key, index));

        const byGroup = new Map();
        rows.forEach((row) => {
            if (!byGroup.has(row.group_key)) {
                byGroup.set(row.group_key, {
                    group_key: row.group_key,
                    primaryRows: [],
                    availableRows: [],
                });
            }
            const bucket = byGroup.get(row.group_key);
            if (row.row_kind === 'input_issue' || row.is_selected) {
                bucket.primaryRows.push(row);
            } else {
                bucket.availableRows.push(row);
            }
        });

        const dir = sort.direction === 'desc' ? -1 : 1;
        const sortRows = (list) => [...list].sort((a, b) => {
            const av = sortAccessor(sort.column, a);
            const bv = sortAccessor(sort.column, b);
            if (typeof av === 'number' && typeof bv === 'number') {
                if (av !== bv) return (av - bv) * dir;
            } else if (compareText(av, bv) !== 0) {
                return compareText(av, bv) * dir;
            }
            const pri = rowPriority(a) - rowPriority(b);
            if (pri !== 0) return pri;
            return compareText(
                a.ticker || a.identifier,
                b.ticker || b.identifier,
            );
        });

        const result = [];
        byGroup.forEach((bucket, groupKey) => {
            const matchedPrimary = bucket.primaryRows.filter(searchMatches);
            const matchedAvailable = bucket.availableRows.filter(searchMatches);
            const issueFiltered = showOnlyIssues
                ? matchedPrimary.filter(isSelectedIssueRow)
                : matchedPrimary;
            // Under focused filters we hide non-primary available options entirely.
            const availableForDisplay = showOnlyIssues
                ? []
                : matchedAvailable;
            if (issueFiltered.length === 0 && availableForDisplay.length === 0) {
                return;
            }
            result.push({
                group_key: groupKey,
                order: orderByKey.has(groupKey) ? orderByKey.get(groupKey) : 9999,
                primaryRows: sortRows(issueFiltered),
                availableRows: sortRows(availableForDisplay),
                groupPriority: issueFiltered.length
                    ? Math.min(...issueFiltered.map(rowPriority))
                    : 4,
            });
        });

        result.sort((a, b) => {
            if (sort.column === 'priority') {
                if (a.groupPriority !== b.groupPriority) {
                    return a.groupPriority - b.groupPriority;
                }
                return a.order - b.order;
            }
            // For a column sort, order groups by their top primary row.
            const ar = a.primaryRows[0];
            const br = b.primaryRows[0];
            if (ar && br) {
                const av = sortAccessor(sort.column, ar);
                const bv = sortAccessor(sort.column, br);
                if (typeof av === 'number' && typeof bv === 'number') {
                    if (av !== bv) return (av - bv) * dir;
                } else if (compareText(av, bv) !== 0) {
                    return compareText(av, bv) * dir;
                }
            }
            return a.order - b.order;
        });
        return result;
    }, [rows, groupsIndex, sort, searchMatches, showOnlyIssues]);

    const hasAnyRows = rows.length > 0;
    const visibleGroupCount = groupsForDisplay.length;

    // Render exactly one row for each submitted security. Country alternatives
    // remain available through the compact Country cell picker; they are not
    // duplicate-looking "candidate" rows pretending to be resolved listings.
    // Fixed-height virtualization preserves the full scroll range without
    // mounting hundreds of MUI component trees.
    const virtualGridEntries = useMemo(() => {
        const entries = [];
        groupsForDisplay.forEach((group, groupIndex) => {
            const shaded = groupIndex % 2 === 1;
            let displayRows = group.primaryRows.length > 0
                ? [group.primaryRows.find((row) => row.is_selected) || group.primaryRows[0]]
                : [];
            if (displayRows.length === 0 && group.availableRows.length > 0) {
                const routeOnly = group.availableRows[0];
                const unresolvedStatus = {
                    state: STATE_NEEDS_ATTENTION,
                    label: 'Needs attention',
                    detail: 'Choose a country to resolve this security.',
                };
                const notEvaluatedStatus = {
                    state: STATE_NOT_EVALUATED,
                    label: 'Not evaluated',
                    detail: 'This check runs after identity resolution.',
                };
                displayRows = [{
                    ...routeOnly,
                    row_key: `${group.group_key}:unresolved-parent`,
                    // Preserve only the submitted identity on this synthetic
                    // parent. Provider-derived route fields belong exclusively
                    // to the Country picker until the user makes a selection.
                    ticker: String(routeOnly.identifier_type || '').toUpperCase() === 'TICKER'
                        ? routeOnly.identifier
                        : null,
                    display_name: null,
                    country: null,
                    is_selected: false,
                    is_editable: false,
                    selection_token: null,
                    selection_state: 'unresolved',
                    msi_listing_label: null,
                    identity: unresolvedStatus,
                    market_data: notEvaluatedStatus,
                    factor_data: notEvaluatedStatus,
                    watchlist_ready: notEvaluatedStatus,
                    portfolio_analysis_ready: notEvaluatedStatus,
                    status_detail: unresolvedStatus.detail,
                }];
            }
            displayRows.forEach((row) => {
                entries.push({
                    key: `listing:${row.row_key}`,
                    type: 'listing',
                    row,
                    available: false,
                    shaded,
                });
            });
        });
        return entries;
    }, [groupsForDisplay]);

    const virtualGridWindow = useMemo(() => {
        const rowCount = virtualGridEntries.length;
        const viewportHeight = Math.max(
            SECURITY_GRID_ROW_HEIGHT,
            Number(securityGridViewport.height) || SECURITY_GRID_DEFAULT_VIEWPORT_HEIGHT,
        );
        const bodyScrollTop = Math.max(
            0,
            Number(securityGridViewport.scrollTop) - SECURITY_GRID_HEADER_HEIGHT,
        );
        const visibleRowCount = Math.ceil(viewportHeight / SECURITY_GRID_ROW_HEIGHT);
        const desiredStart = Math.max(
            0,
            Math.floor(bodyScrollTop / SECURITY_GRID_ROW_HEIGHT)
                - SECURITY_GRID_OVERSCAN_ROWS,
        );
        const maxStart = Math.max(0, rowCount - visibleRowCount);
        const start = Math.min(desiredStart, maxStart);
        const end = Math.min(
            rowCount,
            start + visibleRowCount + (SECURITY_GRID_OVERSCAN_ROWS * 2),
        );
        return {
            start,
            end,
            entries: virtualGridEntries.slice(start, end),
            topSpacerHeight: start * SECURITY_GRID_ROW_HEIGHT,
            bottomSpacerHeight: Math.max(0, rowCount - end) * SECURITY_GRID_ROW_HEIGHT,
        };
    }, [securityGridViewport, virtualGridEntries]);

    const handleSecurityGridScroll = useCallback((event) => {
        const target = event.currentTarget;
        const scrollTop = Number(target.scrollTop) || 0;
        const height = Number(target.clientHeight) || SECURITY_GRID_DEFAULT_VIEWPORT_HEIGHT;
        const movedVertically = securityGridScrollTopRef.current !== scrollTop;
        securityGridScrollTopRef.current = scrollTop;
        const previousPending = securityGridPendingViewportRef.current;
        securityGridPendingViewportRef.current = {
            scrollTop,
            height,
            movedVertically: movedVertically || Boolean(previousPending?.movedVertically),
        };
        if (securityGridScrollFrameRef.current != null) return;
        const scheduleFrame = typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : (callback) => window.setTimeout(callback, 16);
        securityGridScrollFrameRef.current = scheduleFrame(() => {
            securityGridScrollFrameRef.current = null;
            const next = securityGridPendingViewportRef.current;
            securityGridPendingViewportRef.current = null;
            if (!next) return;
            setSecurityGridViewport((current) => (
                current.scrollTop === next.scrollTop && current.height === next.height
                    ? current
                    : { scrollTop: next.scrollTop, height: next.height }
            ));
            if (next.movedVertically) {
                setRevealedGridValue(null);
            }
        });
    }, []);

    const handleSecurityGridKeyDown = useCallback((event) => {
        if (event.target !== event.currentTarget) return;
        const node = event.currentTarget;
        const viewportHeight = Number(node.clientHeight)
            || SECURITY_GRID_DEFAULT_VIEWPORT_HEIGHT;
        const maximumScrollTop = Math.max(
            0,
            SECURITY_GRID_HEADER_HEIGHT
                + (virtualGridEntries.length * SECURITY_GRID_ROW_HEIGHT)
                - viewportHeight,
        );
        let nextScrollTop = null;
        if (event.key === 'PageDown') {
            nextScrollTop = Math.min(
                maximumScrollTop,
                node.scrollTop + Math.max(SECURITY_GRID_ROW_HEIGHT, viewportHeight - 46),
            );
        } else if (event.key === 'PageUp') {
            nextScrollTop = Math.max(
                0,
                node.scrollTop - Math.max(SECURITY_GRID_ROW_HEIGHT, viewportHeight - 46),
            );
        } else if (event.key === 'Home') {
            nextScrollTop = 0;
        } else if (event.key === 'End') {
            nextScrollTop = maximumScrollTop;
        }
        if (nextScrollTop == null) return;
        event.preventDefault();
        node.scrollTop = nextScrollTop;
        securityGridScrollTopRef.current = nextScrollTop;
        setSecurityGridViewport({ scrollTop: nextScrollTop, height: viewportHeight });
        setRevealedGridValue(null);
    }, [virtualGridEntries.length]);

    const cancelPendingSecurityGridScroll = useCallback(() => {
        if (securityGridScrollFrameRef.current == null) {
            securityGridPendingViewportRef.current = null;
            return;
        }
        if (typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(securityGridScrollFrameRef.current);
        } else {
            window.clearTimeout(securityGridScrollFrameRef.current);
        }
        securityGridScrollFrameRef.current = null;
        securityGridPendingViewportRef.current = null;
    }, []);

    useEffect(() => {
        const node = securityGridScrollRef.current;
        if (!node) return undefined;
        const measure = () => {
            const height = Number(node.clientHeight);
            if (!height) return;
            setSecurityGridViewport((current) => (
                current.height === height ? current : { ...current, height }
            ));
        };
        measure();
        if (typeof ResizeObserver !== 'function') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasAnyRows]);

    useEffect(() => () => cancelPendingSecurityGridScroll(), [cancelPendingSecurityGridScroll]);

    useEffect(() => {
        // A publication/filter reset wins over an already queued scroll-frame
        // commit. Otherwise that old frame could replay the previous viewport
        // immediately after the new atomic table is installed.
        cancelPendingSecurityGridScroll();
        const node = securityGridScrollRef.current;
        if (node) node.scrollTop = 0;
        securityGridScrollTopRef.current = 0;
        setSecurityGridViewport((current) => (
            current.scrollTop === 0 ? current : { ...current, scrollTop: 0 }
        ));
    }, [
        resolutionCenter?.active_snapshot_version,
        search,
        showOnlyIssues,
        sort.column,
        sort.direction,
        expectedWatchlistRevisionId,
        cancelPendingSecurityGridScroll,
    ]);

    const handleSort = useCallback((column) => {
        setSort((current) => {
            if (current.column === column) {
                return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { column, direction: 'asc' };
        });
    }, []);

    const showFullGridValue = useCallback((event, key, label, value) => {
        setRevealedGridValue({
            key,
            label,
            value,
            anchorEl: event.currentTarget,
        });
    }, []);

    const renderGridValue = (
        value,
        {
            key,
            label,
            columnKey,
            monospace = false,
            ariaLabel = null,
            color = 'text.primary',
            fontWeight = 400,
        },
    ) => {
        const text = fieldText(value) || '—';
        const approximateCharacterCapacity = Math.max(
            7,
            Math.floor((columnWidth(columnKey) - 14) / (monospace ? 7.2 : 6.8)),
        );
        const clipped = text.length > approximateCharacterCapacity;
        const commonSx = {
            display: 'block',
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.35,
            fontSize: '0.75rem',
            fontFamily: monospace ? 'monospace' : 'inherit',
            fontWeight,
            color,
            textAlign: 'left',
        };
        if (!clipped) {
            return (
                <Typography
                    component="span"
                    aria-label={ariaLabel || undefined}
                    sx={commonSx}
                >
                    {text}
                </Typography>
            );
        }
        return (
            <Box
                component="button"
                type="button"
                title={text}
                aria-label={ariaLabel || `Show full ${label}: ${text}`}
                aria-haspopup="dialog"
                aria-expanded={revealedGridValue?.key === key ? 'true' : 'false'}
                data-testid={`security-resolution-grid-value-${key}`}
                onClick={(event) => showFullGridValue(event, key, label, text)}
                onFocus={(event) => showFullGridValue(event, key, label, text)}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        setRevealedGridValue(null);
                        event.currentTarget.blur();
                    }
                }}
                sx={{
                    ...commonSx,
                    p: 0,
                    m: 0,
                    border: 0,
                    background: 'transparent',
                    cursor: 'zoom-in',
                    '&:focus-visible': {
                        outline: (theme) => `1px solid ${theme.palette.primary.main}`,
                        outlineOffset: 1,
                    },
                }}
            >
                {text}
            </Box>
        );
    };

    const renderResizableHeaderCell = (
        columnKey,
        label,
        content,
        {
            sx = {},
            className = undefined,
            padding = undefined,
            align = undefined,
        } = {},
    ) => (
        <TableCell
            key={columnKey}
            className={className}
            padding={padding}
            align={align}
            data-column-key={columnKey}
            data-column-width={columnWidth(columnKey)}
            title={label}
            sx={{
                ...gridCellSx(columnKey),
                // The resize handle needs an anchored containing block. Keep
                // every header explicitly sticky instead of replacing MUI's
                // stickyHeader positioning with a relative cell.
                position: 'sticky',
                top: 0,
                zIndex: 4,
                ...sx,
            }}
        >
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                {content}
            </Box>
            <Box
                role="separator"
                aria-orientation="vertical"
                aria-label={`Resize ${label} column`}
                tabIndex={0}
                data-testid={`security-resolution-column-resizer-${columnKey}`}
                onMouseDown={(event) => handleColumnResizeStart(event, columnKey)}
                onDoubleClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setColumnWidths((current) => {
                        const next = { ...current };
                        delete next[columnKey];
                        return next;
                    });
                }}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                        event.preventDefault();
                        event.stopPropagation();
                        resizeColumnBy(columnKey, event.key === 'ArrowRight' ? 8 : -8);
                    }
                }}
                sx={{
                    position: 'absolute',
                    zIndex: 8,
                    top: 0,
                    right: 0,
                    width: 10,
                    height: '100%',
                    cursor: 'col-resize',
                    touchAction: 'none',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '18%',
                        bottom: '18%',
                        right: 0,
                        width: '1px',
                        bgcolor: 'divider',
                    },
                    '&:hover::after, &:focus-visible::after': {
                        width: '2px',
                        bgcolor: 'primary.main',
                    },
                    '&:focus-visible': { outline: 'none' },
                }}
            />
        </TableCell>
    );

    const renderListingMetadataCells = (row) => {
        if (!listingLabelsEnabled) return null;
        const label = row?.msi_listing_label;
        const msiTicker = fieldText(label?.ticker) || '—';
        const securityName = fieldText(label?.security_name) || '—';
        const listingMarket = listingMarketText(label);
        const instrumentType = humanizeMsiLabelValue(label?.normalized_instrument_type);
        const listingSelectionStatus = humanizeMsiLabelValue(label?.listing_selection_status);
        return (
            <>
                <TableCell sx={gridCellSx('listing:ticker')}>
                    {renderGridValue(msiTicker, {
                        key: `${row.row_key}:listing:ticker`,
                        label: 'Ticker',
                        columnKey: 'listing:ticker',
                        monospace: true,
                        ariaLabel: `Ticker: ${msiTicker === '—' ? 'Unavailable' : msiTicker}`,
                    })}
                </TableCell>
                <TableCell sx={gridCellSx('listing:security-name')}>
                    {renderGridValue(securityName, {
                        key: `${row.row_key}:listing:security-name`,
                        label: 'Security Name',
                        columnKey: 'listing:security-name',
                    })}
                </TableCell>
                <TableCell sx={gridCellSx('listing:market')}>
                    {renderGridValue(listingMarket, {
                        key: `${row.row_key}:listing:market`,
                        label: 'Listing Market',
                        columnKey: 'listing:market',
                        ariaLabel: `Listing market: ${listingMarket}`,
                    })}
                </TableCell>
                <TableCell sx={gridCellSx('listing:instrument')}>
                    {renderGridValue(instrumentType, {
                        key: `${row.row_key}:listing:instrument`,
                        label: 'Instrument Type',
                        columnKey: 'listing:instrument',
                    })}
                </TableCell>
                <TableCell sx={gridCellSx('listing:selection')}>
                    {renderGridValue(listingSelectionStatus, {
                        key: `${row.row_key}:listing:selection`,
                        label: 'Listing Selection',
                        columnKey: 'listing:selection',
                    })}
                </TableCell>
            </>
        );
    };

    const renderListingRow = (row, { available, shaded, virtualIndex = null }) => {
        const countryOptions = selectionOptionsByGroup.get(row.group_key) || [];
        const countryControl = row?.country_control || {};
        const selectedOptionRowKey = selectedOptionRowKeyByGroup.get(row.group_key) || '';
        const fallbackCountryControlEnabled = row?.is_selected
            ? countryOptions.some((option) => option.row_key !== selectedOptionRowKey)
            : countryOptions.length > 0;
        const identityType = identityTypeText(row);
        const identityValue = identityValueText(row);
        const identityTypeWidth = columnWidth('identity:type');
        const deletionStaged = stagedDeletionGroupKeys.has(row.group_key);
        return (
            <TableRow
                key={row.row_key}
                hover
                style={{ height: SECURITY_GRID_ROW_HEIGHT }}
                data-testid="security-resolution-virtual-row"
                data-virtual-row-index={virtualIndex == null ? undefined : virtualIndex}
                aria-rowindex={virtualIndex == null ? undefined : virtualIndex + 2}
                data-identifier-group-band={shaded ? 'shaded' : 'plain'}
                sx={[
                    identifierGroupBandSx(shaded),
                    {
                        ...(row?.factor_imputed && !deletionStaged ? {
                            '& > .MuiTableCell-root': {
                                background: imputedRowBackground,
                            },
                        } : {}),
                        ...(deletionStaged ? {
                            '& > .MuiTableCell-root': {
                                backgroundColor: (theme) => alpha(
                                    theme.palette.error.main,
                                    0.14,
                                ),
                            },
                        } : {}),
                        '&:hover > .security-resolution-sticky-identity': {
                            background: (theme) => stickyIdentityBackground(
                                theme,
                                {
                                    shaded,
                                    row,
                                    hover: true,
                                },
                            ),
                        },
                    },
                ].filter(Boolean)}
            >
                <TableCell
                    className="security-resolution-sticky-identity"
                    sx={stickyGridCellSx('identity:type', {
                        left: 0,
                        shaded,
                        row,
                    })}
                >
                    {renderGridValue(identityType, {
                        key: `${row.row_key}:identity:type`,
                        label: 'Identity Type',
                        columnKey: 'identity:type',
                    })}
                </TableCell>
                <TableCell
                    className="security-resolution-sticky-identity"
                    sx={stickyGridCellSx('identity:value', {
                        left: identityTypeWidth,
                        shaded,
                        row,
                        edge: true,
                    })}
                >
                    {renderGridValue(identityValue, {
                        key: `${row.row_key}:identity:value`,
                        label: 'Identity Value',
                        columnKey: 'identity:value',
                    })}
                </TableCell>
                <TableCell
                    sx={gridCellSx('country')}
                >
                    {!available ? (
                        <CountrySelectionCell
                            country={countryControl.display_country || row.country}
                            currentCountry={countryControl.current_country || undefined}
                            options={countryOptions}
                            blockedOptions={blockedOptionsByGroup.get(row.group_key) || []}
                            selectedRowKey={selectedOptionRowKey}
                            disabled={applying || (
                                countryControl.enabled == null
                                    ? !fallbackCountryControlEnabled
                                    : countryControl.enabled !== true
                            )}
                            status={countryControl.state || row.selection_state}
                            reason={countryControl.reason || row.status_detail}
                            onSelect={(rowKey) => handleSelectGroupOption(row.group_key, rowKey)}
                        />
                    ) : (
                        <CountryCell country={row.country} />
                    )}
                </TableCell>
                {renderListingMetadataCells(row)}
                <TableCell sx={gridCellSx('status', { textAlign: 'center' })}>
                    <CombinedStatusCell row={row} />
                </TableCell>
                <TableCell sx={gridCellSx('details')}>
                    {renderGridValue(row.status_detail || '—', {
                        key: `${row.row_key}:details`,
                        label: 'Status Details',
                        columnKey: 'details',
                        color: 'text.secondary',
                    })}
                </TableCell>
                <TableCell
                    align="center"
                    sx={gridCellSx('actions', { px: 0.25, textAlign: 'center' })}
                >
                    {(
                        !row?.is_selected
                        && row?.delete_group_token
                        && row?.delete_group_control !== false
                    ) ? (
                        <Tooltip
                            title={deletionStaged
                                ? 'Undo staged dismissal'
                                : 'Stage resolution record dismissal'}
                            arrow
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    color="default"
                                    disabled={applying}
                                    onClick={() => toggleDeletionDraft(row)}
                                    aria-label={deletionStaged
                                        ? 'Undo staged dismissal'
                                        : 'Stage resolution record dismissal'}
                                    sx={{
                                        color: deletionStaged
                                            ? 'error.main'
                                            : 'text.secondary',
                                    }}
                                >
                                    {deletionStaged
                                        ? <UndoOutlinedIcon fontSize="small" />
                                        : <DeleteOutlineIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : null}
                </TableCell>
            </TableRow>
        );
    };

    const sortableHeader = (column, label, tooltip = null) => {
        const sortLabel = (
            <TableSortLabel
                active={sort.column === column}
                direction={sort.column === column ? sort.direction : 'asc'}
                onClick={() => handleSort(column)}
            >
                <span className="security-resolution-header-label">{label}</span>
            </TableSortLabel>
        );
        return tooltip ? (
            <Tooltip title={tooltip} placement="top" arrow>
                <span>{sortLabel}</span>
            </Tooltip>
        ) : sortLabel;
    };

    const summaryChip = (label, value, tone = 'neutral') => (
        <Chip
            size="small"
            color={STATISTIC_CHIP_TONE_TO_COLOR[tone] || 'default'}
            variant="filled"
            label={`${label}: ${Number(value || 0)}`}
            data-testid={statisticChipTestId(label)}
            sx={statisticChipSx(tone)}
        />
    );

    return (
        <Box sx={{ px: { xs: 1.2, md: 1.8 }, py: 1.35, display: 'grid', gap: 1.2 }}>
            {/* Reconciliation review — removed by security-master updates */}
            {!loading && !fetchError && (
                <Box
                    ref={reconciliationSectionRef}
                    data-testid="security-resolution-reconciliation-section"
                    sx={{
                        display: 'grid',
                        gap: 0.9,
                        scrollMarginTop: 24,
                        order: 5,
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={0.8}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontSize: '0.82rem', fontWeight: 800 }}>
                                Removed by security-master updates
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.73rem' }}>
                                Review securities removed after security-master changes.
                            </Typography>
                        </Box>
                        <Chip
                            size="small"
                            color={reconciliationRowCount > 0 ? 'warning' : 'default'}
                            variant="filled"
                            label={`${reconciliationRowCount} pending`}
                            data-testid="security-resolution-reconciliation-pending-count"
                            sx={statisticChipSx(reconciliationRowCount > 0 ? 'pending' : 'neutral')}
                        />
                    </Stack>
                    {reconciliationRowCount === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No removed securities need review.
                        </Typography>
                    ) : (
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={[
                                { borderRadius: 1.5, borderColor: 'divider', overflowX: 'auto' },
                                watchlistTableFrameSx,
                            ]}
                        >
                            <Table size="small" sx={{ minWidth: 760, tableLayout: 'fixed' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: '18%' }}>Identifier</TableCell>
                                        <TableCell sx={{ width: '12%' }}>Ticker</TableCell>
                                        <TableCell sx={{ width: '12%' }}>Country</TableCell>
                                        <TableCell sx={{ width: '20%' }}>Reason</TableCell>
                                        <TableCell sx={{ width: '24%' }}>Successor</TableCell>
                                        <TableCell align="right" sx={{ width: '14%' }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reconciliationRows.map((row) => {
                                        const idType = row.inbound_id_type
                                            ? String(row.inbound_id_type).toUpperCase()
                                            : '';
                                        const idValue = row.inbound_id_value || '';
                                        const country = row.selected_country
                                            || row.uploaded_country_hint
                                            || '';
                                        const candidates = Array.isArray(row.candidates)
                                            ? row.candidates
                                            : [];
                                        const selectedCandidateId = selectedSuccessorByRow[row.row_id]
                                            || candidates[0]?.candidate_id
                                            || '';
                                        const selectedCandidate = candidates.find(
                                            (candidate) => candidate.candidate_id === selectedCandidateId,
                                        ) || candidates[0] || null;
                                        const actionBusy = reviewActionRowId === row.row_id;
                                        const msiSourceAsOfDate = formatDateOnly(
                                            row.msi_source_as_of_date,
                                        );
                                        return (
                                            <TableRow key={row.row_id}>
                                                <TableCell sx={{ overflow: 'hidden' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
                                                        {idType && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                label={idType}
                                                                sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700 }}
                                                            />
                                                        )}
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                fontFamily: 'monospace',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {idValue || '—'}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                                                        {row.ticker || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <CountryCell country={country} />
                                                </TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.35}>
                                                        <Chip
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                            label={row.reason_label || 'Security-master update'}
                                                            sx={{ width: 'fit-content', maxWidth: '100%' }}
                                                        />
                                                        {msiSourceAsOfDate && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                MSI data as of {msiSourceAsOfDate}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    {candidates.length === 0 ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No conservative successor found.
                                                        </Typography>
                                                    ) : candidates.length === 1 ? (
                                                        <Stack spacing={0.25}>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {selectedCandidate?.identifier_label || 'Successor'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {selectedCandidate?.country || '—'}
                                                                {selectedCandidate?.same_country ? ' · same country' : ''}
                                                            </Typography>
                                                        </Stack>
                                                    ) : (
                                                        <FormControl size="small" fullWidth>
                                                            <InputLabel id={`successor-${row.row_id}`}>Successor</InputLabel>
                                                            <Select
                                                                labelId={`successor-${row.row_id}`}
                                                                label="Successor"
                                                                value={selectedCandidateId}
                                                                onChange={(event) => handleSelectSuccessor(
                                                                    row.row_id,
                                                                    event.target.value,
                                                                )}
                                                            >
                                                                {candidates.map((candidate) => (
                                                                    <MenuItem
                                                                        key={candidate.candidate_id}
                                                                        value={candidate.candidate_id}
                                                                    >
                                                                        {candidate.identifier_label} · {candidate.country}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                                        {candidates.length > 0 && (
                                                            <Tooltip title="Replace with selected successor">
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        aria-label="Replace"
                                                                        disabled={actionBusy}
                                                                        onClick={() => handleAcceptSuccessor(row)}
                                                                    >
                                                                        {actionBusy ? (
                                                                            <CircularProgress size={16} />
                                                                        ) : (
                                                                            <SwapHorizIcon fontSize="small" />
                                                                        )}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Acknowledge removal">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="Acknowledge"
                                                                    disabled={actionBusy}
                                                                    onClick={() => handleAcknowledgeReconciliationRow(row)}
                                                                >
                                                                    <DoneIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}

            {/* Summary counts and table controls */}
            {!loading && !fetchError && (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    data-testid="security-resolution-toolbar"
                >
                    <Stack
                        data-testid="security-resolution-stat-chip-group"
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ flex: '1 1 auto', minWidth: 0 }}
                    >
                        {hasAnyRows && summaryChip(
                            'Securities',
                            summary.group_count ?? summary.row_count ?? 0,
                            'neutral',
                        )}
                        {hasAnyRows && summaryChip(
                            'Needs attention',
                            summary.needs_attention_count,
                            'attention',
                        )}
                        {hasAnyRows && Number(summary.imputed_count || 0) > 0 && (
                            <Tooltip
                                arrow
                                title="Verified factor data is unavailable. Estimated data is not supported; review or remove the affected securities."
                            >
                                <span>{summaryChip('Unsupported estimates', summary.imputed_count, 'pending')}</span>
                            </Tooltip>
                        )}
                        {changedGroups.length > 0 && (
                            <Chip
                                size="small"
                                color="primary"
                                variant="filled"
                                label={`Unsaved choices: ${changedGroups.length}`}
                                data-testid="security-resolution-stat-unsaved-changes"
                                sx={statisticChipSx('primary')}
                            />
                        )}
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ ml: { sm: 'auto' }, flexShrink: 0 }}
                    >
                        <Tooltip title={searchPanelOpen ? 'Close search' : 'Search securities'}>
                            <IconButton
                                size="small"
                                color="primary"
                                aria-label={searchPanelOpen
                                    ? 'Close security search'
                                    : 'Open security search'}
                                aria-expanded={searchPanelOpen}
                                onClick={handleToggleSearchPanel}
                                sx={{
                                    width: 34,
                                    height: 34,
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    boxShadow: searchPanelOpen ? 2 : 1,
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    },
                                }}
                            >
                                <SearchIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <FormControlLabel
                            sx={{ m: 0, ml: 0.25, flexShrink: 0 }}
                            control={(
                                <Checkbox
                                    size="small"
                                    checked={showOnlyIssues}
                                    onChange={(event) => setShowOnlyIssues(event.target.checked)}
                                />
                            )}
                            label={(
                                <Typography variant="body2" color="text.secondary">
                                    Issues only
                                </Typography>
                            )}
                        />
                    </Stack>
                </Stack>
            )}

            {!loading && !fetchError && (
                <Typography variant="caption" color="text.secondary" data-testid="security-resolution-scope">
                    Security Resolution shows only your current published Watchlist version. Choices here do not change shared MSI identity records.
                </Typography>
            )}

            {!loading && !fetchError && (
                <Collapse in={searchPanelOpen} unmountOnExit>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <TextField
                            autoFocus
                            size="small"
                            placeholder="Search ticker, company, identifier, country"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            sx={{
                                width: { xs: '100%', sm: 360 },
                                maxWidth: '100%',
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{ 'aria-label': 'Search securities' }}
                        />
                    </Box>
                </Collapse>
            )}


            {loading && (
                <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} thickness={5} aria-hidden="true" />
                    <Typography variant="body2" color="text.secondary">
                        Loading security resolution status...
                    </Typography>
                </Box>
            )}

            {!loading && Boolean(fetchError) && (
                <Alert severity="warning">{fetchError}</Alert>
            )}

            {!loading && !fetchError && status === 'degraded' && (
                <Alert severity="info">
                    {resolutionCenter?.message || 'Security resolution data is temporarily unavailable.'}
                </Alert>
            )}

            {!loading && !fetchError && status === 'stale' && (
                <Alert severity="info">
                    {resolutionCenter?.message
                        || 'Showing the last successful results while the latest status is refreshed.'}
                </Alert>
            )}

            {errorMessage && (
                <Alert severity="error" onClose={() => setErrorMessage('')}>
                    {errorMessage}
                </Alert>
            )}

            {!loading && !fetchError && !hasAnyRows && status !== 'degraded' && (
                <Typography variant="body2" color="text.secondary">
                    No watchlist securities found.
                </Typography>
            )}

            {!loading && !fetchError && hasAnyRows && (
                <>
                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        ref={securityGridScrollRef}
                        onScroll={handleSecurityGridScroll}
                        onKeyDown={handleSecurityGridKeyDown}
                        role="region"
                        tabIndex={0}
                        aria-label="Scrollable Security Resolution results"
                        data-testid="security-resolution-table-scroll"
                        sx={[
                            watchlistTableFrameSx,
                            {
                                borderRadius: 1,
                                borderColor: 'divider',
                                maxHeight: 'min(64vh, 660px)',
                                overflow: 'auto',
                                overscrollBehavior: 'contain',
                                scrollbarGutter: 'stable',
                                isolation: 'isolate',
                            },
                        ]}
                    >
                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            minWidth: tableMinWidth,
                            tableLayout: 'fixed',
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            isolation: 'isolate',
                            '& .MuiTableCell-root': {
                                height: SECURITY_GRID_ROW_HEIGHT,
                                minHeight: SECURITY_GRID_ROW_HEIGHT,
                                px: 1,
                                py: 0.5,
                                lineHeight: 1.35,
                                borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                                fontSize: '0.75rem',
                            },
                            '& .MuiTableBody-root .MuiTableRow-root': {
                                height: SECURITY_GRID_ROW_HEIGHT,
                            },
                            '& .MuiTableBody-root .MuiTableRow-root[data-virtual-row-index] > .MuiTableCell-root': {
                                height: SECURITY_GRID_ROW_HEIGHT,
                                maxHeight: SECURITY_GRID_ROW_HEIGHT,
                                overflow: 'hidden',
                            },
                            // Global table striping uses DOM :nth-of-type, which is
                            // unstable when virtual spacer rows enter or leave the
                            // mounted window. Drive this table's stripe from the
                            // row's durable group band instead so scrolling cannot
                            // make visible rows flash between colors.
                            '& .MuiTableBody-root .MuiTableRow-root[data-identifier-group-band="plain"], & .MuiTableBody-root .MuiTableRow-root[data-identifier-group-band="plain"] > .MuiTableCell-root:not([data-edited="true"])': {
                                backgroundColor: (theme) => (
                                    usesAppStyleGrammar(theme)
                                        ? MSI_PALETTE.surface
                                        : theme.palette.background.paper
                                ),
                            },
                            '& .MuiTableBody-root .MuiTableRow-root[data-identifier-group-band="shaded"], & .MuiTableBody-root .MuiTableRow-root[data-identifier-group-band="shaded"] > .MuiTableCell-root:not([data-edited="true"])': {
                                backgroundColor: (theme) => (
                                    usesAppStyleGrammar(theme)
                                        ? MSI_PALETTE.surfaceSubtle
                                        : alpha(
                                            theme.palette.text.primary,
                                            theme.palette.mode === 'dark' ? 0.055 : 0.022,
                                        )
                                ),
                            },
                            '& .MuiTableBody-root .MuiTableCell-root:not(.security-resolution-sticky-identity)': {
                                position: 'relative',
                                zIndex: 0,
                            },
                            '& .MuiTableHead-root .MuiTableCell-root': {
                                height: SECURITY_GRID_HEADER_HEIGHT,
                                minHeight: SECURITY_GRID_HEADER_HEIGHT,
                                py: 0.5,
                                bgcolor: (theme) => (
                                    usesAppStyleGrammar(theme)
                                        ? MSI_COMPONENT_COLORS.tableHeader
                                        : theme.palette.background.default
                                ),
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                            '& .MuiTableSortLabel-root': {
                                minWidth: 0,
                                maxWidth: '100%',
                                font: 'inherit',
                                letterSpacing: 'inherit',
                            },
                            // A header narrower than its label ends in an
                            // ellipsis; the sort arrow never shrinks. The arrow
                            // drops its trailing margin because the default
                            // column widths are sized for label plus arrow.
                            '& .security-resolution-header-label': {
                                display: 'block',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                            '& .MuiTableSortLabel-icon': {
                                flexShrink: 0,
                                mr: 0,
                            },
                            '& .MuiIconButton-root': { p: 0.5 },
                        }}
                        aria-label="Security resolution status"
                        aria-rowcount={virtualGridEntries.length + 1
                            + (visibleGroupCount === 0 ? 1 : 0)}
                    >
                        <TableHead>
                            <TableRow>
                                {renderResizableHeaderCell(
                                    'identity:type',
                                    'Identity Type',
                                    sortableHeader('identity:type', 'Identity Type'),
                                    {
                                        className: 'security-resolution-sticky-identity',
                                        sx: stickyGridCellSx('identity:type', {
                                            left: 0,
                                            header: true,
                                        }),
                                    },
                                )}
                                {renderResizableHeaderCell(
                                    'identity:value',
                                    'Identity Value',
                                    sortableHeader('identity:value', 'Identity Value'),
                                    {
                                        className: 'security-resolution-sticky-identity',
                                        sx: stickyGridCellSx('identity:value', {
                                            left: columnWidth('identity:type'),
                                            header: true,
                                            edge: true,
                                        }),
                                    },
                                )}
                                {renderResizableHeaderCell(
                                    'country',
                                    'Country',
                                    sortableHeader('country', 'Country'),
                                )}
                                {listingLabelsEnabled && (
                                    <>
                                        {renderResizableHeaderCell(
                                            'listing:ticker',
                                            'Ticker',
                                            sortableHeader('listing:ticker', 'Ticker'),
                                        )}
                                        {renderResizableHeaderCell(
                                            'listing:security-name',
                                            'Security Name',
                                            sortableHeader('listing:security-name', 'Security Name'),
                                        )}
                                        {renderResizableHeaderCell(
                                            'listing:market',
                                            'Listing Market',
                                            sortableHeader(
                                                'listing:market',
                                                'Listing Market',
                                                PRIMARY_VENUE_NOTE,
                                            ),
                                        )}
                                        {renderResizableHeaderCell(
                                            'listing:instrument',
                                            'Instrument Type',
                                            sortableHeader('listing:instrument', 'Instrument Type'),
                                        )}
                                        {renderResizableHeaderCell(
                                            'listing:selection',
                                            'Listing Selection',
                                            sortableHeader('listing:selection', 'Listing Selection'),
                                        )}
                                    </>
                                )}
                                {renderResizableHeaderCell(
                                    'status',
                                    'Status',
                                    sortableHeader('status', 'Status'),
                                    { align: 'center' },
                                )}
                                {renderResizableHeaderCell(
                                    'details',
                                    'Status Details',
                                    sortableHeader('details', 'Status Details'),
                                )}
                                {renderResizableHeaderCell(
                                    'actions',
                                    'Actions',
                                    <span className="security-resolution-header-label">Actions</span>,
                                    { align: 'center' },
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleGroupCount === 0 && (
                                <TableRow aria-rowindex={2}>
                                    <TableCell colSpan={tableColumnCount}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                            No securities match the current filters.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {virtualGridWindow.topSpacerHeight > 0 && (
                                <TableRow
                                    aria-hidden="true"
                                    data-testid="security-resolution-virtual-top-spacer"
                                    style={{ height: virtualGridWindow.topSpacerHeight }}
                                >
                                    <TableCell
                                        colSpan={tableColumnCount}
                                        style={{
                                            height: virtualGridWindow.topSpacerHeight,
                                            minHeight: virtualGridWindow.topSpacerHeight,
                                        }}
                                        sx={{
                                            p: 0,
                                            border: 0,
                                        }}
                                    />
                                </TableRow>
                            )}
                            {virtualGridWindow.entries.map((entry, offset) => {
                                const virtualIndex = virtualGridWindow.start + offset;
                                return renderListingRow(entry.row, {
                                    available: false,
                                    shaded: entry.shaded,
                                    virtualIndex,
                                });
                            })}
                            {virtualGridWindow.bottomSpacerHeight > 0 && (
                                <TableRow
                                    aria-hidden="true"
                                    data-testid="security-resolution-virtual-bottom-spacer"
                                    style={{ height: virtualGridWindow.bottomSpacerHeight }}
                                >
                                    <TableCell
                                        colSpan={tableColumnCount}
                                        style={{
                                            height: virtualGridWindow.bottomSpacerHeight,
                                            minHeight: virtualGridWindow.bottomSpacerHeight,
                                        }}
                                        sx={{
                                            p: 0,
                                            border: 0,
                                        }}
                                    />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </TableContainer>
                    <Popper
                        open={Boolean(revealedGridValue?.anchorEl)}
                        anchorEl={revealedGridValue?.anchorEl || null}
                        placement="bottom-start"
                        modifiers={[
                            { name: 'flip', enabled: true },
                            {
                                name: 'preventOverflow',
                                enabled: true,
                                options: { padding: 10 },
                            },
                        ]}
                        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
                    >
                        <Paper
                            role="dialog"
                            aria-label={`${revealedGridValue?.label || 'Cell'} full value`}
                            data-testid="security-resolution-full-value"
                            elevation={7}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 0.75,
                                maxWidth: 'min(520px, calc(100vw - 24px))',
                                p: 0.75,
                                border: 1,
                                borderColor: 'primary.main',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
                            >
                                {revealedGridValue?.value || ''}
                            </Typography>
                            <IconButton
                                size="small"
                                aria-label="Close full cell value"
                                onClick={() => setRevealedGridValue(null)}
                                sx={{ mt: -0.35, mr: -0.35 }}
                            >
                                <Typography component="span" aria-hidden="true">×</Typography>
                            </IconButton>
                        </Paper>
                    </Popper>
                </>
            )}

            <ProviderIdentityReviewSection
                groups={resolutionCenter?.provider_identity_review_groups || []}
                referenceReviews={resolutionCenter?.provider_identity_reviews || []}
                selectedFactorVendor={selectedFactorVendor}
                expectedRevisionId={resolutionCenter?.provider_review_base_revision_id}
                pendingRevisionId={resolutionCenter?.provider_review_pending_revision_id}
                sessionScopeToken={sessionScopeToken}
                onRefresh={onRefresh}
            />

            {/* Apply footer */}
            {!loading && !fetchError && pendingChangeCount > 0 && (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="flex-end"
                    sx={{ order: 6 }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
                        {`${pendingChangeCount} securit${pendingChangeCount === 1 ? 'y' : 'ies'} with unsaved changes.`}
                    </Typography>
                    <Button
                        size="small"
                        variant="text"
                        onClick={handleClearChanges}
                        disabled={applying}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => { void handleApply(); }}
                        disabled={applying}
                        startIcon={applying ? <CircularProgress size={14} thickness={6} /> : null}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        {applying ? 'Saving...' : 'Save changes'}
                    </Button>
                </Stack>
            )}
            <Snackbar
                open={Boolean(applyResultMessage?.text)}
                autoHideDuration={APPLY_RESULT_VISIBLE_MS}
                onClose={() => setApplyResultMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={applyResultMessage?.severity || 'info'}
                    variant="filled"
                    onClose={() => setApplyResultMessage(null)}
                >
                    {applyResultMessage?.text || ''}
                </Alert>
            </Snackbar>
        </Box>
    );
}

WatchlistSecurityStatusTable.propTypes = {
    selectedFactorVendor: PropTypes.string,
    resolutionCenter: PropTypes.shape({
        loading: PropTypes.bool,
        loaded: PropTypes.bool,
        error: PropTypes.string,
        status: PropTypes.string,
        message: PropTypes.string,
        active_snapshot_version: PropTypes.number,
        summary: PropTypes.object,
        watchlist_field_contract: PropTypes.object,
        groups: PropTypes.array,
        rows: PropTypes.array,
        provider_identity_reviews: PropTypes.arrayOf(PropTypes.shape({
            review_key: PropTypes.string,
            review_kind: PropTypes.oneOf(['ambiguity', 'continuity', 'name_disagreement']),
            provider: PropTypes.string,
            current_security: PropTypes.object,
            provider_security: PropTypes.shape({
                ticker: PropTypes.string,
                name: PropTypes.string,
            }),
            action_available: PropTypes.bool,
            action_unavailable_reason: PropTypes.string,
            recovery_guidance: PropTypes.string,
            retry_only: PropTypes.bool,
            retry_action: PropTypes.oneOf(['select', 'use_updated']),
            remove_capability: PropTypes.shape({
                delete_token: PropTypes.string,
                removes_portfolio: PropTypes.bool,
            }),
            options: PropTypes.array,
        })),
        portfolio_analysis_admission: PropTypes.shape({
            factor_data_available: PropTypes.bool,
            readiness_is_final: PropTypes.bool,
        }),
        reconciliation_review: PropTypes.shape({
            rows: PropTypes.array,
            row_count: PropTypes.number,
            actionable_count: PropTypes.number,
        }),
    }),
    onRefresh: PropTypes.func,
    focusTarget: PropTypes.string,
};
